import { netzereignis, messstelle, freischaltung, konfig } from './../../../utils/apiTypes';
import { netzereignisseState } from './netzereignisseTypes';
import { AnyAction, createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '../../../app/store';
import { deleteWithAuth, getWithAuth, postJsonWithAuth, putWithAuth } from '../../../utils/api';
import { openSnackbar } from '../../../features/snackbar/globalSnackbarSlice';
import { isValidDate, logError } from '../../../utils/utils';
import { GridSortModel } from '@material-ui/data-grid';
import { errorHandler } from '../../../utils/errorHandling';

const currentTime = new Date();
currentTime.setUTCSeconds(0, 0);

export const netzereignisDummy: netzereignis = {
    id: 0,
    ereignisueberschrift: "",
    messstelle: "",
    grund: "",
    freischaltung: [],
    zeit: currentTime,
    ende: currentTime,
    geplant: true,
    ereignistext: "",
    kommentar: "",
}

const konfigDummy: konfig = {
    id: 0,
    kategorie: "",
    name: "",
    wert: "2",
    default: true,
}

const initialState: netzereignisseState = {
    netzereignisseListe: [],
    aktuellesNetzereignis: netzereignisDummy,
    altesNetzereignis: netzereignisDummy,
    konfig: konfigDummy,
    editEntry: false,
    newEntry: false,
    editDirty: 0,
    viewMode: false,
    error: [],
    messstellen: [],
    freischaltungen: [],
    netzereignisLoading: false,
    konfigLoading: false,
    messstellenLoading: false,
    freischaltungLoading: false,
    sorting: [
        {
            field: 'zeit',
            sort: 'asc',
        },
    ]
}

const netzereignisseSlice = createSlice({
    name: "netzereignisse",
    initialState,
    reducers: {
        setMessstellen: (state, action: PayloadAction<Array<messstelle>>) => {
            state.messstellen = action.payload;
        },
        setFreischaltungen: (state, action: PayloadAction<Array<freischaltung>>) => {
            state.freischaltungen = action.payload;
        },
        updateNetzereignisse: (state, action: PayloadAction<netzereignis>) => {
            const updateIndex = state.netzereignisseListe.findIndex(
                (x: netzereignis) => x.id == action.payload.id
            );
            const firstPart = state.netzereignisseListe.slice(0, updateIndex);
            let lastPart = Array<netzereignis>();
            if (updateIndex + 1 < state.netzereignisseListe.length)
                lastPart = state.netzereignisseListe.slice(updateIndex + 1);
            state.netzereignisseListe = [...firstPart, action.payload, ...lastPart];
        },

        addToNetzereignisse: (state, action: PayloadAction<netzereignis>) => {
            const netzereignisse = state.netzereignisseListe;
            netzereignisse.push(action.payload);
            state.netzereignisseListe = [...netzereignisse].sort((a, b) => ((new Date(b.zeit)).getTime() - (new Date(a.zeit)).getTime()));
        },
        removeFromNetzereignisse: (state, action: PayloadAction<netzereignis>) => {
            const updateIndex = state.netzereignisseListe.findIndex(
                (x) => x.id == action.payload.id
            );
            const firstPart = state.netzereignisseListe.slice(0, updateIndex);
            let lastPart = Array<netzereignis>();
            if (updateIndex + 1 < state.netzereignisseListe.length)
                lastPart = state.netzereignisseListe.slice(updateIndex + 1);
            state.netzereignisseListe = [...firstPart, ...lastPart];
        },
        setNetzereignisse: (state, action: PayloadAction<Array<netzereignis>>) => {
            state.netzereignisseListe = action.payload;
        },
        setAltesNetzereignis: (state, action: PayloadAction<netzereignis>) => {
            state.altesNetzereignis = action.payload;
        },
        setAktuellesNetzereignis: (state, action: PayloadAction<netzereignis>) => {
            state.aktuellesNetzereignis = action.payload;
            state.altesNetzereignis = action.payload;
        },
        resetAktuellesNetzereignis: (state) => {
            state.aktuellesNetzereignis = state.altesNetzereignis;
        },
        setKonfig: (state, action: PayloadAction<konfig>) => {
            state.konfig = action.payload;
        },
        setEditEntry: (state) => {
            state.editEntry = true;
        },
        resetEditEntry: (state) => {
            state.editEntry = false;
        },
        setNewEntry: (state) => {
            state.newEntry = true;
        },
        resetNewEntry: (state) => {
            state.newEntry = false;
        },
        resetEditDirty: (state) => {
            state.editDirty = 0;
        },
        setViewMode: (state) => {
            state.viewMode = true;
        },
        resetViewMode: (state) => {
            state.viewMode = false;
        },
        setError: (state, action: PayloadAction<string>) => {
            if (!state.error.includes(action.payload)) {
                state.error.push(action.payload);
            }
        },
        resetError: (state, action: PayloadAction<string>) => {
            if (state.error.includes(action.payload)) {
                state.error.splice(state.error.indexOf(action.payload), 1)
            }
        },
        setEreignisueberschrift: (state, action: PayloadAction<string>) => {
            state.aktuellesNetzereignis.ereignisueberschrift = action.payload;
            state.editDirty =
                state.aktuellesNetzereignis.ereignisueberschrift == state.altesNetzereignis.ereignisueberschrift
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setMessstelle: (state, action: PayloadAction<string>) => {
            state.aktuellesNetzereignis.messstelle = action.payload;
            state.editDirty =
                state.aktuellesNetzereignis.messstelle == state.altesNetzereignis.messstelle
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setFreischaltung: (state, action: PayloadAction<string[]>) => {
            state.aktuellesNetzereignis.freischaltung = action.payload;
            state.editDirty =
                state.aktuellesNetzereignis.freischaltung == state.altesNetzereignis.freischaltung
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setGeplant: (state, action: PayloadAction<string>) => {
            const geplantBool = action.payload === "geplant"
            state.aktuellesNetzereignis.geplant = geplantBool;
            state.editDirty =
                state.aktuellesNetzereignis.geplant == state.altesNetzereignis.geplant
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setEreignistext: (state, action: PayloadAction<string>) => {
            state.aktuellesNetzereignis.ereignistext = action.payload;
            state.editDirty =
                state.aktuellesNetzereignis.ereignistext == state.altesNetzereignis.ereignistext
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setGrund: (state, action: PayloadAction<string>) => {
            state.aktuellesNetzereignis.grund = action.payload;
            state.editDirty =
                state.aktuellesNetzereignis.grund == state.altesNetzereignis.grund
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setKommentar: (state, action: PayloadAction<string>) => {
            state.aktuellesNetzereignis.kommentar = action.payload;
            state.editDirty =
                state.aktuellesNetzereignis.kommentar == state.altesNetzereignis.kommentar
                    ? state.editDirty - 1
                    : state.editDirty + 1;
        },
        setStartDatum: (state, action: PayloadAction<Date | null>) => {
            if (action.payload != null) {
                state.aktuellesNetzereignis.zeit = new Date(action.payload);
                if (isValidDate(action.payload)) {
                    const endzeit = state.aktuellesNetzereignis.ende;
                    if (endzeit < state.aktuellesNetzereignis.zeit) {
                        state.aktuellesNetzereignis.ende = state.aktuellesNetzereignis.zeit;
                    }
                    state.editDirty =
                        state.aktuellesNetzereignis.zeit ==
                            state.altesNetzereignis.zeit
                            ? state.editDirty - 1
                            : state.editDirty + 1;
                    if (state.error.includes("Startdatum")) {
                        state.error.splice(state.error.indexOf("Startdatum"), 1);
                    }
                } else {
                    if (!state.error.includes("Startdatum")) {
                        state.error.push("Startdatum");
                    }
                }
            }
        },
        setEndDatum: (state, action: PayloadAction<Date | null>) => {
            if (action.payload != null) {
                state.aktuellesNetzereignis.ende = new Date(action.payload);
                if (isValidDate(action.payload)) {
                    state.editDirty =
                        state.aktuellesNetzereignis.ende ==
                            state.altesNetzereignis.ende
                            ? state.editDirty - 1
                            : state.editDirty + 1;
                    if (state.error.includes("Enddatum")) {
                        state.error.splice(state.error.indexOf("Enddatum"), 1);
                    }
                } else {
                    if (!state.error.includes("Enddatum")) {
                        state.error.push("Enddatum");
                    }
                }
            }
        },
        setKonfigWert: (state, action: PayloadAction<string>) => {
            state.konfig.wert = action.payload;
        },
        setKonfigId: (state, action: PayloadAction<number>) => {
            state.konfig.id = action.payload;
        },
        setNetzereignisLoading: (state, action: PayloadAction<boolean>) => {
            state.netzereignisLoading = action.payload;
        },
        setKonfigLoading: (state, action: PayloadAction<boolean>) => {
            state.konfigLoading = action.payload;
        },
        setMessstellenLoading: (state, action: PayloadAction<boolean>) => {
            state.messstellenLoading = action.payload;
        },
        setFreischaltungLoading: (state, action: PayloadAction<boolean>) => {
            state.konfigLoading = action.payload;
        },
        setSorting: (state, action: PayloadAction<GridSortModel>) => {
            state.sorting = action.payload;
        },
    }
});

export const {
    setAktuellesNetzereignis,
    setEditEntry,
    resetEditDirty,
    resetError,
    resetEditEntry,
    resetNewEntry,
    resetViewMode,
    setError,
    setNewEntry,
    setViewMode,
    addToNetzereignisse,
    removeFromNetzereignisse,
    resetAktuellesNetzereignis,
    setAltesNetzereignis,
    setKonfig,
    setKonfigWert,
    setKonfigId,
    setNetzereignisse,
    updateNetzereignisse,
    setNetzereignisLoading,
    setKonfigLoading,
    setMessstellenLoading,
    setFreischaltungLoading,
    setEndDatum,
    setEreignistext,
    setEreignisueberschrift,
    setGeplant,
    setKommentar,
    setStartDatum,
    setMessstelle,
    setMessstellen,
    setGrund,
    setFreischaltung,
    setFreischaltungen,
    setSorting
} = netzereignisseSlice.actions

export const selectNetzsereignisseListe = (state: RootState): Array<netzereignis> => state.netzereignisse.netzereignisseListe
export const selectAktuellesNetzereignis = (state: RootState): netzereignis =>
    state.netzereignisse.aktuellesNetzereignis;
export const selectKonfig = (state: RootState): konfig =>
    state.netzereignisse.konfig;
export const selectEditEntry = (state: RootState): boolean =>
    state.netzereignisse.editEntry;
export const selectNewEntry = (state: RootState): boolean => state.netzereignisse.newEntry;
export const selectEditDirty = (state: RootState): number =>
    state.netzereignisse.editDirty;
export const selectViewMode = (state: RootState): boolean => state.netzereignisse.viewMode;
export const selectError = (state: RootState): boolean => state.netzereignisse.error.length > 0;
export const selectMessstellen = (state: RootState): Array<messstelle> => state.netzereignisse.messstellen;
export const selectFreischaltungen = (state: RootState): Array<freischaltung> => state.netzereignisse.freischaltungen;
export const selectNetzereignisLoading = (state: RootState): boolean => state.netzereignisse.netzereignisLoading;
export const selectKonfigLoading = (state: RootState): boolean => state.netzereignisse.konfigLoading;
export const selectMessstellenLoading = (state: RootState): boolean => state.netzereignisse.messstellenLoading;
export const selectFreischaltungLoading = (state: RootState): boolean => state.netzereignisse.freischaltungLoading;
export const selectSorting = (state: RootState): GridSortModel => state.netzereignisse.sorting;


export const getNetzereignisse = (idString: string | undefined) => (dispatch: AppDispatch): void => {
    dispatch(setNetzereignisLoading(true));
    getWithAuth("NETZEREIGNIS", {})
        .then((response: Array<netzereignis>) => {
            dispatch(setNetzereignisse(response));
            if (idString) {
                const id = Number(idString);
                const aktuellesNetzereignis = response.find(x => x.id == id);
                if (aktuellesNetzereignis) dispatch(updateAktuellesNetzereignis(aktuellesNetzereignis));
            }
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setNetzereignisLoading(false)));
};

export const getNetzereignisseWithKonfig = () => (dispatch: AppDispatch): void => {
    let konfig = konfigDummy;
    dispatch(setNetzereignisLoading(true));
    getWithAuth("KONFIG", { name: "anzahl_wochen_netzereignisse", kategorie: "konfig" })
        .then((response: konfig[]) => {
            konfig = response[0];

            const d = new Date();
            const days = Number(konfig.wert) * 7;

            const from_Number = d.setDate(d.getDate() - days);
            const from_String = new Date(from_Number).toISOString();
            const to_Number = d.setDate(d.getDate() + (2*days));
            const to_String = new Date(to_Number).toISOString();

            getWithAuth("NETZEREIGNIS", { from: from_String, to: to_String })
                .then((response) => {
                    dispatch(setNetzereignisse(response));
                })
                .catch((error) => {
                    logError(error);
                    errorHandler(error, dispatch);
                });
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setNetzereignisLoading(false)));
};

export const getMessstellen = () => (dispatch: AppDispatch): void => {
    dispatch(setMessstellenLoading(true));
    getWithAuth("MESSSTELLE", {})
        .then((response) => {
            dispatch(setMessstellen(response));
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setMessstellenLoading(false)));
};

export const getFreischaltungen = () => (dispatch: AppDispatch): void => {
    dispatch(setFreischaltungLoading(true));
    getWithAuth("FREISCHALTUNG", {})
        .then((response) => {
            dispatch(setFreischaltungen(response));
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setFreischaltungLoading(false)));
};

export const getKonfig = () => (dispatch: AppDispatch): void => {
    dispatch(setKonfigLoading(true));
    getWithAuth("KONFIG", { name: "anzahl_wochen_netzereignisse", kategorie: "konfig" })
        .then((response: konfig[]) => {
            dispatch(setKonfig(response[0]));
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setKonfigLoading(false)));
};

export const updateAktuellesNetzereignis = (netzereignis: netzereignis): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch,
    getState: () => RootState
) => {
    if (!getState().netzereignisse.editEntry) {
        dispatch(setAktuellesNetzereignis(netzereignis));
        dispatch(setViewMode());
    }
};

export const updateNetzereignis = (netzereignis: netzereignis): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setNetzereignisLoading(true));
    putWithAuth("NETZEREIGNISID", {}, netzereignis, netzereignis.id.toString())
        .then(() => {
            dispatch(
                openSnackbar({
                    message: `Netzereignis '${netzereignis.ereignisueberschrift}' wurde erfolgreich geändert.`,
                    severity: "success",
                    duration: 6000,
                })
            );
            dispatch(setAltesNetzereignis(netzereignis));
            dispatch(updateNetzereignisse(netzereignis));
            dispatch(resetEditEntry());
            dispatch(resetNewEntry());
            dispatch(resetEditDirty());
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setNetzereignisLoading(false)));
};

export const updateKonfig = (konfig: konfig): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    if (konfig.default) {
        postJsonWithAuth("KONFIG", {}, konfig)
            .then((response: number) => {
                dispatch(setKonfig({ ...konfig, id: response, default: false }));
                dispatch(
                    openSnackbar({
                        message: `Wochenanzeige auf der Startseite wurde mit '${konfig.wert}' Wochen geändert.`,
                        severity: "success",
                        duration: 6000,
                    })
                );
            })
            .catch((error) => {
                logError(error);
                errorHandler(error, dispatch);
            });
    } else {
        putWithAuth("KONFIGID", {}, konfig, konfig.id.toString())
            .then(() => {
                dispatch(
                    openSnackbar({
                        message: `Wochenanzeige auf der Startseite wurde mit '${konfig.wert}' Wochen geändert.`,
                        severity: "success",
                        duration: 6000,
                    })
                );
            })
            .catch((error) => {
                logError(error);
                errorHandler(error, dispatch);
            });
    }
};

export const discardChanges = () => (dispatch: AppDispatch): void => {
    dispatch(resetAktuellesNetzereignis());
    dispatch(resetEditEntry());
    dispatch(resetNewEntry());
    dispatch(resetEditDirty());
};

export const discardNew = () => (dispatch: AppDispatch): void => {
    dispatch(resetAktuellesNetzereignis());
    dispatch(resetEditEntry());
    dispatch(resetNewEntry());
    dispatch(resetEditDirty());
    dispatch(resetViewMode());
};

export const startNewEntry = () => (dispatch: AppDispatch): void => {
    dispatch(setNewEntry());
    dispatch(setAktuellesNetzereignis(netzereignisDummy));
    dispatch(setEditEntry());
    dispatch(setViewMode());
};

export const createNetzereignis = (netzereignis: netzereignis): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setNetzereignisLoading(true));
    postJsonWithAuth("NETZEREIGNIS", {},

        {
            id: netzereignis.id,
            ereignisueberschrift: netzereignis.ereignisueberschrift,
            messstelle: netzereignis.messstelle,
            grund: netzereignis.grund,
            geplant: netzereignis.geplant,
            zeit: netzereignis.zeit,
            ende: netzereignis.ende,
            ereignistext: netzereignis.ereignistext,
            freischaltung: netzereignis.freischaltung,
            kommentar: netzereignis.kommentar
        })
        .then((id: number) => {
            dispatch(
                openSnackbar({
                    message: `Netzereignis '${netzereignis.ereignisueberschrift}' wurde erfolgreich angelegt.`,
                    severity: "success",
                    duration: 6000,
                }));
            const netzereignisCopy = Object.assign({}, netzereignis);
            netzereignisCopy.id = id;
            dispatch(setAltesNetzereignis(netzereignisCopy));
            dispatch(setAktuellesNetzereignis(netzereignisDummy));
            dispatch(addToNetzereignisse(netzereignisCopy));
            dispatch(resetEditEntry());
            dispatch(resetNewEntry());
            dispatch(resetEditDirty());
            dispatch(resetViewMode());
        })
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setNetzereignisLoading(false)));

};

export const deleteNetzereignis = (netzereignis: netzereignis): ThunkAction<void, RootState, unknown, AnyAction> => (
    dispatch: AppDispatch
) => {
    dispatch(setNetzereignisLoading(true));
    deleteWithAuth("NETZEREIGNISID", {}, netzereignis.id.toString())
        .then(() => dispatch(setAktuellesNetzereignis(netzereignisDummy)))
        .then(() => dispatch(removeFromNetzereignisse(netzereignis)))
        .then(() => dispatch(resetEditEntry()))
        .then(() => dispatch(resetEditDirty()))
        .then(() => dispatch(resetViewMode()))
        .then(() => dispatch(
            openSnackbar({
                message: `Netzereignis '${netzereignis.ereignisueberschrift}' wurde erfolgreich gelöscht.`,
                severity: "success",
                duration: 6000,
            })
        ))
        .catch((error) => {
            logError(error);
            errorHandler(error, dispatch);
        }).finally(() => dispatch(setNetzereignisLoading(false)));

};

export default netzereignisseSlice.reducer